import React, { useState, useRef, useEffect } from 'react';
import { X, Mail, Lock, User, ArrowRight, Loader2, Eye, EyeOff, Building, CheckCircle } from 'lucide-react';
import {
  login,
  signup,
  requestSignupCode,
  verifySignupCode,
  requestPasswordResetCode,
  verifyPasswordResetCode,
  resetPassword
} from '../api/auth';
import { useAppLanguage } from '../context/AppLanguageContext';

const AUTH_TEXT = {
  en: {
    login: 'Login',
    signup: 'Sign Up',
    verifyEmail: 'Verify Email',
    forgotPassword: 'Reset Password',
    enterCode: 'Enter Code',
    welcomeBack: 'Welcome Back',
    createAccount: 'Create Account',
    welcome: 'Welcome',
    emailAddress: 'Email Address',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    fullName: 'Full Name',
    companyOptional: 'Company (Optional)',
    yourCompany: 'Your Company',
    forgotPasswordLink: 'Forgot password?',
    signIn: 'Sign In',
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: 'Already have an account?',
    continue: 'Continue',
    verifyCreate: 'Verify & Create Account',
    backToSignup: 'Back to signup',
    resetHelp: "Enter your email address and we'll send you a code to reset your password.",
    sendResetCode: 'Send Reset Code',
    backToSignIn: 'Back to sign in',
    enterCodeSent: 'Enter the code sent to',
    newPassword: 'New Password',
    resetPassword: 'Reset Password',
    back: 'Back',
    verificationCodeSent: 'Verification code sent to your email!',
    accountCreated: 'Account created successfully!',
    resetCodeSent: 'Reset code sent to your email!',
    passwordResetSuccess: 'Password reset successfully! You can now sign in.',
    invalidCredentials: 'Invalid email or password.',
    passwordsMismatch: 'Passwords do not match.',
    passwordMin: 'Password must be at least 6 characters.',
    sendVerificationFailed: 'Unable to send verification code.',
    enter6Digit: 'Please enter the 6-digit code.',
    invalidVerification: 'Invalid verification code.',
    sendResetFailed: 'Unable to send reset code.',
    invalidResetCode: 'Invalid reset code.',
    english: 'English',
    vietnamese: 'Tiếng Việt',
  },
  vi: {
    login: 'Đăng nhập',
    signup: 'Đăng ký',
    verifyEmail: 'Xác minh email',
    forgotPassword: 'Đặt lại mật khẩu',
    enterCode: 'Nhập mã',
    welcomeBack: 'Chào mừng quay lại',
    createAccount: 'Tạo tài khoản',
    welcome: 'Chào mừng',
    emailAddress: 'Địa chỉ email',
    password: 'Mật khẩu',
    confirmPassword: 'Xác nhận mật khẩu',
    fullName: 'Họ và tên',
    companyOptional: 'Công ty (không bắt buộc)',
    yourCompany: 'Công ty của bạn',
    forgotPasswordLink: 'Quên mật khẩu?',
    signIn: 'Đăng nhập',
    dontHaveAccount: 'Chưa có tài khoản?',
    alreadyHaveAccount: 'Đã có tài khoản?',
    continue: 'Tiếp tục',
    verifyCreate: 'Xác minh & tạo tài khoản',
    backToSignup: 'Quay lại đăng ký',
    resetHelp: 'Nhập email của bạn và chúng tôi sẽ gửi mã để đặt lại mật khẩu.',
    sendResetCode: 'Gửi mã đặt lại',
    backToSignIn: 'Quay lại đăng nhập',
    enterCodeSent: 'Nhập mã đã gửi tới',
    newPassword: 'Mật khẩu mới',
    resetPassword: 'Đặt lại mật khẩu',
    back: 'Quay lại',
    verificationCodeSent: 'Đã gửi mã xác minh tới email của bạn!',
    accountCreated: 'Tạo tài khoản thành công!',
    resetCodeSent: 'Đã gửi mã đặt lại tới email của bạn!',
    passwordResetSuccess: 'Đặt lại mật khẩu thành công! Bạn có thể đăng nhập ngay.',
    invalidCredentials: 'Email hoặc mật khẩu không đúng.',
    passwordsMismatch: 'Mật khẩu xác nhận không khớp.',
    passwordMin: 'Mật khẩu phải có ít nhất 6 ký tự.',
    sendVerificationFailed: 'Không thể gửi mã xác minh.',
    enter6Digit: 'Vui lòng nhập mã gồm 6 chữ số.',
    invalidVerification: 'Mã xác minh không hợp lệ.',
    sendResetFailed: 'Không thể gửi mã đặt lại.',
    invalidResetCode: 'Mã đặt lại không hợp lệ.',
    english: 'English',
    vietnamese: 'Tiếng Việt',
  },
};

const AuthScreen = ({ onClose, onLoginSuccess, initialMode = 'login' }) => {
  const { language, setLanguage } = useAppLanguage();
  const t = AUTH_TEXT[language] || AUTH_TEXT.en;
  // Form mode state
  const [mode, setMode] = useState(initialMode); // 'login', 'signup', 'verify-email', 'forgot-password', 'reset-password'

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Code input refs
  const codeInputRefs = useRef([]);

  // Focus first code input when entering verification mode
  useEffect(() => {
    if (mode === 'verify-email' || mode === 'reset-password') {
      codeInputRefs.current[0]?.focus();
    }
  }, [mode]);

  // Handle verification code input
  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newCode = [...verificationCode];
    newCode[index] = value.slice(-1); // Only keep last digit
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      setVerificationCode(pastedData.split(''));
      codeInputRefs.current[5]?.focus();
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login({ email, password });

      // Store session
      localStorage.setItem('claw9_session', JSON.stringify({
        email,
        timestamp: Date.now()
      }));

      onLoginSuccess?.(result.data);
    } catch (err) {
      setError(err.message || t.invalidCredentials);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle signup - request verification code
  const handleSignupStart = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t.passwordsMismatch);
      return;
    }

    if (password.length < 6) {
      setError(t.passwordMin);
      return;
    }

    setIsLoading(true);

    try {
      await requestSignupCode({ email });
      setMode('verify-email');
      setSuccessMessage(t.verificationCodeSent);
    } catch (err) {
      setError(err.message || t.sendVerificationFailed);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle verify email and complete signup
  const handleVerifyAndSignup = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError(t.enter6Digit);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Verify code
      await verifySignupCode({ email, code });

      // Complete signup
      const result = await signup({
        email,
        password,
        name,
        company
      });

      // Store session
      localStorage.setItem('claw9_session', JSON.stringify({
        email,
        timestamp: Date.now()
      }));

      setSuccessMessage(t.accountCreated);
      setTimeout(() => {
        onLoginSuccess?.(result.data);
      }, 1000);
    } catch (err) {
      setError(err.message || t.invalidVerification);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password - request reset code
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await requestPasswordResetCode({ email });
      setMode('reset-password');
      setSuccessMessage(t.resetCodeSent);
      setVerificationCode(['', '', '', '', '', '']);
    } catch (err) {
      setError(err.message || t.sendResetFailed);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reset password
  const handleResetPassword = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError(t.enter6Digit);
      return;
    }

    if (newPassword.length < 6) {
      setError(t.passwordMin);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Verify code first
      await verifyPasswordResetCode({ email, code });

      // Reset password
      await resetPassword({ email, code, newPassword });

      setSuccessMessage(t.passwordResetSuccess);
      setTimeout(() => {
        setMode('login');
        setPassword('');
        setNewPassword('');
        setVerificationCode(['', '', '', '', '', '']);
        setSuccessMessage('');
      }, 2000);
    } catch (err) {
      setError(err.message || t.invalidResetCode);
    } finally {
      setIsLoading(false);
    }
  };

  // Render verification code inputs
  const renderCodeInputs = () => (
    <div className="flex justify-center gap-2">
      {verificationCode.map((digit, index) => (
        <input
          key={index}
          ref={el => codeInputRefs.current[index] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleCodeChange(index, e.target.value)}
          onKeyDown={(e) => handleCodeKeyDown(index, e)}
          onPaste={index === 0 ? handleCodePaste : undefined}
          className="w-12 h-14 text-center text-2xl font-bold bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#ff4d4d] focus:ring-2 focus:ring-[#ff4d4d]/30 transition-all"
        />
      ))}
    </div>
  );

  // Get modal title
  const getTitle = () => {
    switch (mode) {
      case 'login': return t.welcomeBack;
      case 'signup': return t.createAccount;
      case 'verify-email': return t.verifyEmail;
      case 'forgot-password': return t.forgotPassword;
      case 'reset-password': return t.enterCode;
      default: return t.welcome;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-[#0d1424] rounded-2xl shadow-2xl w-full max-w-md border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-white font-semibold text-lg">
              {getTitle()}
            </h2>
            <div className="flex rounded-lg bg-white/10 p-1">
              <button type="button" onClick={() => setLanguage('vi')} className={`rounded-md px-2 py-1 text-xs ${language === 'vi' ? 'bg-white/20 text-white' : 'text-white/75'}`}>{t.vietnamese}</button>
              <button type="button" onClick={() => setLanguage('en')} className={`rounded-md px-2 py-1 text-xs ${language === 'en' ? 'bg-white/20 text-white' : 'text-white/75'}`}>{t.english}</button>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg text-sm flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* LOGIN MODE */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-white/80 text-sm mb-2">{t.emailAddress}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] transition-colors"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/80 text-sm mb-2">{t.password}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] transition-colors"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => { setMode('forgot-password'); setError(''); }}
                  className="text-sm text-[#ff4d4d] hover:underline"
                >
                  {t.forgotPasswordLink}
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] py-4 rounded-lg font-semibold text-white flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#ff4d4d]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {t.signIn}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <div className="text-center text-white/60 text-sm pt-2">
                {t.dontHaveAccount}{' '}
                <button
                  type="button"
                  onClick={() => { setMode('signup'); setError(''); }}
                  className="text-[#ff4d4d] hover:underline font-medium"
                >
                  {t.signup}
                </button>
              </div>
            </form>
          )}

          {/* SIGNUP MODE */}
          {mode === 'signup' && (
            <form onSubmit={handleSignupStart} className="space-y-4">
              <div>
                <label className="block text-white/80 text-sm mb-2">{t.fullName}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] transition-colors"
                    placeholder="John Doe"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/80 text-sm mb-2">{t.companyOptional}</label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] transition-colors"
                    placeholder={t.yourCompany}
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/80 text-sm mb-2">{t.emailAddress}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] transition-colors"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/80 text-sm mb-2">{t.password}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] transition-colors"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-white/80 text-sm mb-2">{t.confirmPassword}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] transition-colors"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] py-4 rounded-lg font-semibold text-white flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#ff4d4d]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {t.continue}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <div className="text-center text-white/60 text-sm pt-2">
                {t.alreadyHaveAccount}{' '}
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); }}
                  className="text-[#ff4d4d] hover:underline font-medium"
                >
                  {t.signIn}
                </button>
              </div>
            </form>
          )}

          {/* VERIFY EMAIL MODE */}
          {mode === 'verify-email' && (
            <div className="space-y-6">
              <p className="text-center text-white/70 text-sm">
                {t.verificationCodeSent}<br />
                <span className="font-semibold text-white">{email}</span>
              </p>

              {renderCodeInputs()}

              <button
                onClick={handleVerifyAndSignup}
                disabled={isLoading || verificationCode.join('').length !== 6}
                className="w-full bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] py-4 rounded-lg font-semibold text-white flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#ff4d4d]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {t.verifyCreate}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  onClick={() => { setMode('signup'); setError(''); setVerificationCode(['', '', '', '', '', '']); }}
                  className="text-sm text-white/50 hover:text-white"
                >
                  ← {t.backToSignup}
                </button>
              </div>
            </div>
          )}

          {/* FORGOT PASSWORD MODE */}
          {mode === 'forgot-password' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-center text-white/70 text-sm mb-4">
                {t.resetHelp}
              </p>

              <div>
                <label className="block text-white/80 text-sm mb-2">{t.emailAddress}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] transition-colors"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] py-4 rounded-lg font-semibold text-white flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#ff4d4d]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {t.sendResetCode}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); }}
                  className="text-sm text-white/50 hover:text-white"
                >
                  ← {t.backToSignIn}
                </button>
              </div>
            </form>
          )}

          {/* RESET PASSWORD MODE */}
          {mode === 'reset-password' && (
            <div className="space-y-6">
              <p className="text-center text-white/70 text-sm">
                {t.enterCodeSent}<br />
                <span className="font-semibold text-white">{email}</span>
              </p>

              {renderCodeInputs()}

              <div>
                <label className="block text-white/80 text-sm mb-2">{t.newPassword}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] transition-colors"
                    placeholder="••••••••"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                onClick={handleResetPassword}
                disabled={isLoading || verificationCode.join('').length !== 6 || newPassword.length < 6}
                className="w-full bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] py-4 rounded-lg font-semibold text-white flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#ff4d4d]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {t.resetPassword}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  onClick={() => { setMode('forgot-password'); setError(''); setVerificationCode(['', '', '', '', '', '']); }}
                  className="text-sm text-white/50 hover:text-white"
                >
                  ← {t.back}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
